import React, { createContext, useContext, ReactNode } from 'react';
import { URL_PARAMS } from '../constants';
import { extractUrlParameters } from '../utils';

interface GameInfo {
  date: string | null;
  gameId: string | null;
}

interface UserLevelState {
  set: number;
  level: number;
  question: number;
}

interface ApiMethods {
  getGameInfoFromUrl: () => GameInfo;
  getUserLevelStateFromUrl: () => UserLevelState;
  getTokenFromUrl: () => string | null;
  saveGameProgress: (set: number, level: number, score: number, totalScore: number) => Promise<void>;
}

interface ApiProviderProps {
  children: ReactNode;
}

const ApiContext = createContext<ApiMethods | null>(null);

const createApiMethods = (): ApiMethods => ({
  getGameInfoFromUrl: (): GameInfo => {
    const url = window.location.href;
    const gameInfoMatch = url.match(/\/games\/game\/([^/]+)\/([^/]+)\//); 
    return {
      date: gameInfoMatch ? gameInfoMatch[1] : null,
      gameId: gameInfoMatch ? gameInfoMatch[2] : null
    };
  },

  getUserLevelStateFromUrl: (): UserLevelState => {
    const { set, level, question } = extractUrlParameters();
    return {
      set: parseInt(set || '1'),
      level: parseInt(level || '1'),
      question: parseInt(question || '1')
    };
  },

  getTokenFromUrl: (): string | null => {
    const { token } = extractUrlParameters();
    return token;
  },

  saveGameProgress: async (set: number, level: number, score: number, totalScore: number): Promise<void> => {
    const apiMethods = createApiMethods();
    const { date, gameId } = apiMethods.getGameInfoFromUrl();
    const token = apiMethods.getTokenFromUrl();

    if (!gameId) {
      console.error('Could not extract gameId from URL');
      return;
    }

    if (!token) {
      console.error('Could not extract token from URL');
      return;
    }

    try {
      const response = await fetch('https://api.kinzy.club/api/v1/games/storage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          gameId: gameId,
          set: set,
          level: level,
          score: score,
          totalScore: totalScore,
          date: date
        })
      });

      if (response.ok) {
        console.log(`Progress saved: Set ${set}, Level ${level}, Score ${score}, Total Score ${totalScore}`);
      } else {
        console.error('Failed to save progress:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }
});

const ApiProvider: React.FC<ApiProviderProps> = ({ children }) => {
  const apiMethods = createApiMethods();

  return (
    <ApiContext.Provider value={apiMethods}>
      {children}
    </ApiContext.Provider>
  );
};

const useApi = (): ApiMethods => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
};

export type { ApiProviderProps, GameInfo, UserLevelState, ApiMethods };
export { ApiProvider, useApi };
export default ApiProvider;